import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RefreshCw, Loader2, ChevronLeft, ChevronRight, Trash2, X, CheckSquare, Square, PenSquare, Star, Paperclip, Mail, Inbox, Archive, SlidersHorizontal, MailCheck, Bell, AlertTriangle, Clock, Info, ArrowLeft, Reply, ReplyAll, Forward, User, Users, Calendar, ExternalLink, RotateCcw, CheckCircle2, CheckCircle } from 'lucide-react'
import { Card } from '../atoms'
import { cn } from '../atoms'
import { useEmails, useEmail, useEmailStats, useGmailSync, useGmailStatus, useDeleteEmail, useMarkAsRead, useToggleStarEmail, useSyncReadStatus, useUpdateCriticidad, useToggleEmailHandled } from '../../hooks/useEmails'
import { useResizableColumns, type ColumnDef } from '../../hooks/useResizableColumns'
import { useDebounce } from '../../hooks/useDebounce'
import { LiveIndicator } from '../molecules/LiveIndicator'
import { SearchBar } from '../molecules/SearchBar'
import { FilterChips } from '../molecules/FilterChips'
import { HighlightedText } from '../molecules/HighlightedText'
import { UNREAD_TAB_VALUE } from '../molecules/CriticidadTabs'
import { IndicatorCard } from '../molecules/IndicatorCard'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'
import { useAuth } from '../../store/auth.store'
import { useImpersonation } from '../../store/impersonation.store'
import { useComposeModal } from '../organisms/ComposeModal'
import { parseGmailQuery, type GmailSearchFilters } from '../../utils/searchQuery'
import { AdvancedSearchModal } from '../molecules/AdvancedSearchModal'
import { sanitizeHtmlBody } from '../../utils/assetUrl'

function getInitials(nameOrEmail: string): string {
  if (!nameOrEmail) return '?'
  const cleaned = nameOrEmail.trim()
  if (cleaned.includes('@')) {
    const local = cleaned.split('@')[0]
    return local.slice(0, 2).toUpperCase()
  }
  const parts = cleaned.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return cleaned.slice(0, 2).toUpperCase()
}

function getAvatarColor(nameOrEmail: string): string {
  const colors = [
    'bg-rose-100 text-rose-700',
    'bg-amber-100 text-amber-700',
    'bg-emerald-100 text-emerald-700',
    'bg-sky-100 text-sky-700',
    'bg-violet-100 text-violet-700',
    'bg-fuchsia-100 text-fuchsia-700',
    'bg-teal-100 text-teal-700',
    'bg-orange-100 text-orange-700',
  ]
  let hash = 0
  for (let i = 0; i < (nameOrEmail || '').length; i++) {
    hash = nameOrEmail.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

function stripHtml(html: string): string {
  if (!html) return ''
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

function sanitizePreviewHtml(html: string): string {
  if (!html) return ''
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, '')
    .replace(/<object[^>]*>[\s\S]*?<\/object>/gi, '')
    .replace(/<embed[^>]*\/?>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/background="[^"]*"/gi, '')
    .replace(/style="[^"]*(position:\s*fixed|position:\s*absolute)[^"]*"/gi, '')
}

function formatEmailDate(date: string): string {
  const d = new Date(date)
  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (sameDay) {
    return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })
  }
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  if (isYesterday) return 'Ayer'
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays < 7) {
    return d.toLocaleDateString('es-CO', { weekday: 'short' })
  }
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })
}

const limitOptions = [10, 20, 50, 100]
const criticidadClasses: Record<string, string> = {
  CRITICO_MAXIMO: 'bg-red-100 text-red-700',
  CRITICO: 'bg-red-50 text-red-600',
  ALTO: 'bg-orange-50 text-orange-600',
  MEDIO: 'bg-amber-50 text-amber-600',
  BAJO: 'bg-emerald-50 text-emerald-600',
  INFORMATIVO: 'bg-blue-50 text-blue-600',
}

function formatCriticidad(value?: string | null) {
  if (!value) return '—'
  return value === 'CRITICO_MAXIMO' ? 'CRÍTICO MÁXIMO' : value.charAt(0) + value.slice(1).toLowerCase()
}

const emailColumns: ColumnDef[] = [
  { key: 'check', label: '', defaultWidth: 72, minWidth: 60, maxWidth: 80, resizable: false },
  { key: 'sender', label: 'Remitente', defaultWidth: 220, minWidth: 140, maxWidth: 380 },
  { key: 'subject', label: 'Asunto / Vista previa', defaultWidth: 480, minWidth: 220, maxWidth: 800 },
  { key: 'criticidad', label: 'Criticidad', defaultWidth: 130, minWidth: 90, maxWidth: 180 },
  { key: 'date', label: 'Recibido', defaultWidth: 120, minWidth: 90, maxWidth: 180 },
]

export function EmailsPage() {
  const navigate = useNavigate()
  const { open: openCompose } = useComposeModal()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)
  const labelFilter = searchParams.get('label') || undefined
  const criticidadFilter = searchParams.get('criticidad') || undefined
  const isUnreadTab = labelFilter === UNREAD_TAB_VALUE

  const debouncedSearch = useDebounce(search, 300)

  // Parsear la query para extraer filtros (chips + aplicar a Prisma)
  const { filters: parsedFilters } = useMemo(() => parseGmailQuery(search), [search])

  // Combinar debouncedSearch con label (que viene de URL params, no de la barra)
  // NOTA: el label del sidebar se envía como param dedicado (?label=X).
  // Lo quitamos del search para evitar duplicación en el backend.
  const finalSearch = useMemo(() => {
    if (isUnreadTab) {
      // Para el tab "No leídos", agregar is:unread al search
      const base = debouncedSearch || ''
      if (!base.includes('is:unread')) return `is:unread ${base}`.trim()
      return base
    }
    if (!debouncedSearch && !labelFilter) return undefined
    const cleaned = labelFilter
      ? debouncedSearch.replace(new RegExp(`\\b(?:label|etiqueta):${labelFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi'), '').replace(/\s+/g, ' ').trim()
      : debouncedSearch
    return cleaned || undefined
  }, [debouncedSearch, labelFilter, isUnreadTab])

  const effectiveLabel = isUnreadTab ? undefined : labelFilter

  const { data, isLoading, dataUpdatedAt, isFetching } = useEmails({
    page,
    limit,
    search: finalSearch,
    label: effectiveLabel,
    criticidad: criticidadFilter,
  })
  const { data: emailStats } = useEmailStats()
  const { data: selectedEmail, isLoading: isSelectedEmailLoading } = useEmail(selectedEmailId || '')
  const { data: gmailStatusRaw } = useGmailStatus()
  const gmailStatus = gmailStatusRaw as any
  const gmailSync = useGmailSync()
  const syncReadStatus = useSyncReadStatus()
  const deleteMutation = useDeleteEmail()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()
  const markAsRead = useMarkAsRead()
  const toggleStar = useToggleStarEmail()
  const updateCriticidad = useUpdateCriticidad()
  const toggleHandled = useToggleEmailHandled()
  const { user } = useAuth()
  const { impersonateMailboxId, impersonateMailboxName } = useImpersonation()

  const { widths: colWidths, handleResizeStart: onResizeStart, resetWidths: resetColWidths } = useResizableColumns({
    columns: emailColumns,
    storageKey: 'emails-table-columns-v2',
  })

  // Ancho del panel de lista cuando el preview está abierto (arrastrable)
  const [listPanelWidth, setListPanelWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('emails-list-panel-width')
      return saved ? Number(saved) : 460
    } catch { return 460 }
  })

  const handleSplitResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = listPanelWidth

    const onMove = (ev: MouseEvent) => {
      const diff = ev.clientX - startX
      const newWidth = Math.min(720, Math.max(320, startWidth + diff))
      setListPanelWidth(newWidth)
      try { localStorage.setItem('emails-list-panel-width', String(newWidth)) } catch {}
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const pagination = data?.pagination
  const totalPages = pagination?.totalPages || 1
  const currentPage = pagination?.page || 1

  // Reset página cuando cambia la búsqueda
  useEffect(() => { setPage(1) }, [search, finalSearch])

  const goToPage = (p: number) => {
    if (p >= 1 && p <= totalPages) setPage(p)
  }

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setPage(1)
  }

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
  }

  const toggleSelectAll = () => {
    const allIds = (data?.data || []).map((e: any) => e.id)
    if (selectedIds.size === allIds.length) setSelectedIds(new Set())
    else setSelectedIds(new Set(allIds))
  }

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`¿Eliminar ${selectedIds.size} emails?`)) return
    let deleted = 0
    for (const id of selectedIds) {
      try {
        await deleteMutation.mutateAsync({ emailId: id, fromGmail: false })
        deleted++
      } catch { /* ignore */ }
    }
    setSelectedIds(new Set())
    successToast(`${deleted} emails eliminados`)
  }

  const handleOpenEmail = (email: any) => {
    if (!email.isRead) markAsRead.mutate(email.id)
    setSelectedEmailId(email.id)
  }

  const handleToggleStar = (e: React.MouseEvent, email: any) => {
    e.stopPropagation()
    toggleStar.mutate({ id: email.id, isStarred: email.isStarred })
  }

  const handleRemoveFilter = (key: keyof GmailSearchFilters) => {
    // Reconstruir el search removiendo el operador del key
    const newFilters = { ...parsedFilters }
    delete newFilters[key]
    const newSearch = buildSearchFromFilters(newFilters)
    setSearch(newSearch)
  }

  const handleClearAll = () => {
    setSearch('')
    setSearchParams({})
    setPage(1)
  }

  const selectSummaryFilter = (label?: string) => {
    if (!label) {
      // Total correos: resets specific card filters (label and criticidad)
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.delete('label')
        next.delete('criticidad')
        return next
      })
    } else if (label === UNREAD_TAB_VALUE) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (next.get('label') === UNREAD_TAB_VALUE) {
          next.delete('label')
        } else {
          next.set('label', UNREAD_TAB_VALUE)
          next.delete('criticidad')
        }
        return next
      })
    } else {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (next.get('criticidad') === label) {
          next.delete('criticidad')
        } else {
          next.set('criticidad', label)
          next.delete('label')
        }
        return next
      })
    }
    setPage(1)
  }

  // Lista compacta (estilo móvil) — usada en móvil Y en desktop cuando el preview está abierto
  const renderCompactList = () => (
    <div className="divide-y divide-gray-100 dark:divide-gray-700">
      {data?.data?.map((email: any) => {
        const isSelected = selectedIds.has(email.id)
        const isActive = selectedEmailId === email.id
        const fromDisplay = email.fromName || email.from
        const preview = stripHtml(email.preview || email.body || '').slice(0, 120)
        const previewHtml = sanitizePreviewHtml(email.htmlBody || email.body || '').slice(0, 400)
        return (
          <div
            key={email.id}
            onClick={() => handleOpenEmail(email)}
            className={cn(
              'group relative flex items-start gap-2 px-3 py-3 cursor-pointer transition-colors min-w-0',
              'hover:bg-gray-50 dark:hover:bg-gray-800/60',
              email.isRead ? 'bg-white dark:bg-gray-900' : 'bg-blue-50/30 dark:bg-blue-950/20',
              isSelected && 'bg-blue-50/70 dark:bg-blue-900/30',
              isActive && 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-l-primary-500',
            )}
          >
            <div className="flex items-center gap-1 shrink-0 pt-0.5">
              <button onClick={(e) => { e.stopPropagation(); toggleSelect(email.id) }} className={cn('p-0.5', isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}>
                {isSelected ? <CheckSquare className="w-4 h-4 text-primary-600" /> : <Square className="w-4 h-4 text-gray-400" />}
              </button>
              <button onClick={(e) => handleToggleStar(e, email)} className="p-0.5">
                <Star className={cn('w-4 h-4', email.isStarred ? 'fill-amber-400 text-amber-400' : 'text-gray-400 opacity-0 group-hover:opacity-100')} />
              </button>
              <div className={cn('w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ring-1 shrink-0', getAvatarColor(fromDisplay), email.isRead ? 'ring-transparent' : 'ring-primary-200')}>
                {getInitials(fromDisplay)}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2 min-w-0">
                <p className={cn('truncate text-sm', email.isRead ? 'text-gray-700 dark:text-gray-300' : 'font-semibold text-gray-900 dark:text-gray-100')}>
                  {fromDisplay}
                </p>
                <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">{formatEmailDate(email.date || email.receivedAt)}</span>
              </div>
              <div className="flex items-center gap-1.5 min-w-0">
                <p className={cn('truncate text-sm flex-1', email.isRead ? 'text-gray-500 dark:text-gray-400' : 'font-medium text-gray-800 dark:text-gray-200')}>
                  {email.subject || '(Sin asunto)'}
                </p>
                {email.hasAttachments && <Paperclip className="w-3.5 h-3.5 shrink-0 text-gray-400" />}
              </div>
              {previewHtml ? (
                <div className="email-preview-html mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2 overflow-hidden" dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : preview ? (
                <p className="truncate text-xs text-gray-500 dark:text-gray-400 mt-0.5">{preview}</p>
              ) : null}
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {email.criticidad && (
                  <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold', criticidadClasses[email.criticidad] || 'bg-slate-100 text-slate-600')}>
                    {formatCriticidad(email.criticidad)}
                  </span>
                )}
                {email.isHandled || email.case?.status?.isFinal ? (
                  <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300">
                    <CheckCircle2 className="w-2.5 h-2.5" /> Gestionado
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  // Quick filters: chips que el usuario puede tappear (Gmail style)
  const quickFilters = [
    { op: 'is:unread', label: 'No leídos', active: !!parsedFilters.isUnread },
    { op: 'is:starred', label: 'Destacados', active: !!parsedFilters.isStarred },
    { op: 'has:attachment', label: 'Con adjuntos', active: !!parsedFilters.hasAttachment },
  ]
  const toggleQuickFilter = (op: string) => {
    const tokens = search.split(/\s+/).filter(Boolean).filter(t => t !== op)
    if (!parsedFilters[op.split(':')[0] as keyof GmailSearchFilters] && !search.includes(op)) {
      tokens.push(op)
    }
    setSearch(tokens.join(' '))
  }

  const getPageNumbers = () => {
    const pages: number[] = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1)
    }
    for (let i = start; i <= end; i++) pages.push(i)
    return pages
  }

  return (
    <div className="flex flex-col h-full">
      <div className={cn(
        'flex-1 min-w-0',
        selectedEmailId
          ? 'xl:flex xl:flex-col xl:gap-0 xl:p-2'
          : 'p-4 space-y-4'
      )}>
      {/* Header + Filtros — shrinks when preview open */}
      <div className={cn(
        selectedEmailId ? 'xl:shrink-0 xl:mb-2' : '',
        'space-y-3',
      )}>
      {/* ── HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-primary-600" />
            Comunicaciones
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Gestión centralizada de correos y mensajes · {pagination?.total || 0} resultados
            {isUnreadTab && (
              <>
                {' '}
                · filtrado por: <span className="font-mono text-xs text-indigo-600 dark:text-indigo-400">No leídos</span>
                <button
                  onClick={() => { setSearchParams({}); setPage(1) }}
                  className="ml-2 text-xs text-gray-500 hover:text-red-600"
                  title="Quitar filtro"
                >
                  (quitar)
                </button>
              </>
            )}
            {labelFilter && !isUnreadTab && (
              <>
                {' '}
                · filtrado por: <span className="font-mono text-xs text-primary-600 dark:text-primary-400">{labelFilter}</span>
                <button
                  onClick={() => { setSearchParams({}); setPage(1) }}
                  className="ml-2 text-xs text-gray-500 hover:text-red-600"
                  title="Quitar filtro"
                >
                  (quitar)
                </button>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <LiveIndicator lastUpdatedAt={dataUpdatedAt} isRefreshing={isFetching} />
          {gmailStatus?.configured && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/50">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Gmail
            </span>
          )}
          <button
            onClick={() => {
              gmailSync.mutate(impersonateMailboxId, {
                onSuccess: (res: any) => {
                  const total = res?.total ?? res?.newEmails ?? res?.synced ?? 0
                  successToast(
                    impersonateMailboxName
                      ? `Sincronización completada (${impersonateMailboxName}): ${total} emails nuevos`
                      : `Sincronización completada: ${total} emails nuevos`
                  )
                },
                onError: (err: any) => {
                  errorToast(err?.message || 'Error al sincronizar emails')
                },
              })
            }}
            disabled={gmailSync.isPending || !gmailStatus?.configured}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title={!gmailStatus?.configured ? 'Gmail no configurado' : 'Sincronizar'}
          >
            {gmailSync.isPending ? <Loader2 className="w-4 h-4 animate-spin text-primary-600" /> : <RefreshCw className="w-4 h-4" />}
            <span className="hidden sm:inline">
              {gmailSync.isPending ? 'Sincronizando...' : impersonateMailboxName ? `Sincronizar ${impersonateMailboxName}` : 'Sincronizar'}
            </span>
          </button>
          <button
            onClick={() => {
              syncReadStatus.mutate(impersonateMailboxId || undefined, {
                onSuccess: () => {
                  successToast('Estado de no leídos sincronizado con éxito')
                },
                onError: (err: any) => {
                  errorToast(err?.message || 'Error al sincronizar estado de no leídos')
                },
              })
            }}
            disabled={syncReadStatus.isPending}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title="Sincronizar estado leído/no leído con Gmail"
          >
            {syncReadStatus.isPending ? <Loader2 className="w-4 h-4 animate-spin text-primary-600" /> : <MailCheck className="w-4 h-4" />}
            <span className="hidden sm:inline">
              {syncReadStatus.isPending ? 'Verificando...' : 'No leídos'}
            </span>
          </button>
          <button
            onClick={openCompose}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
          >
            <PenSquare className="w-4 h-4" />
            Redactar
          </button>
        </div>
      </div>

      {gmailSync.isPending && (
        <div className="rounded-lg bg-blue-50 dark:bg-blue-900/30 p-3 text-sm text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          <span>Sincronizando correos{impersonateMailboxName ? ` de ${impersonateMailboxName}` : ''}... Por favor espere.</span>
        </div>
      )}

      {gmailSync.isSuccess && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/30 p-3 text-sm text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/50">
          ✓ Sincronización completada{impersonateMailboxName ? ` (${impersonateMailboxName})` : ''}: <strong>{(gmailSync.data as any)?.total ?? (gmailSync.data as any)?.newEmails ?? (gmailSync.data as any)?.synced ?? 0}</strong> emails nuevos
        </div>
      )}

      {syncReadStatus.isSuccess && (
        <div className="rounded-lg bg-blue-50 dark:bg-blue-900/30 p-3 text-sm text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50">
          ✓ No leídos sincronizados con Gmail
        </div>
      )}

      {/* ── RESUMEN DE COMUNICACIONES ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <EmailStatCard
          label="Total correos"
          value={(emailStats as any)?.total ?? pagination?.total ?? 0}
          icon={Mail}
          tone="blue"
          active={!labelFilter && !criticidadFilter}
          onClick={() => selectSummaryFilter()}
        />
        <EmailStatCard
          label="No leídos"
          value={(emailStats as any)?.unread ?? 0}
          icon={MailCheck}
          tone="slate"
          active={isUnreadTab}
          onClick={() => selectSummaryFilter(UNREAD_TAB_VALUE)}
        />
        <EmailStatCard
          label="Crítico máximo"
          value={(emailStats as any)?.byCriticality?.CRITICO_MAXIMO ?? 0}
          icon={Bell}
          tone="red"
          active={criticidadFilter === 'CRITICO_MAXIMO'}
          onClick={() => selectSummaryFilter('CRITICO_MAXIMO')}
        />
        <EmailStatCard
          label="Críticos"
          value={(emailStats as any)?.byCriticality?.CRITICO ?? 0}
          icon={AlertTriangle}
          tone="red"
          active={criticidadFilter === 'CRITICO'}
          onClick={() => selectSummaryFilter('CRITICO')}
        />
        <EmailStatCard
          label="Altos"
          value={(emailStats as any)?.byCriticality?.ALTO ?? 0}
          icon={AlertTriangle}
          tone="amber"
          active={criticidadFilter === 'ALTO'}
          onClick={() => selectSummaryFilter('ALTO')}
        />
        <EmailStatCard
          label="Medios"
          value={(emailStats as any)?.byCriticality?.MEDIO ?? 0}
          icon={Clock}
          tone="green"
          active={criticidadFilter === 'MEDIO'}
          onClick={() => selectSummaryFilter('MEDIO')}
        />
        <EmailStatCard
          label="Bajos"
          value={(emailStats as any)?.byCriticality?.BAJO ?? 0}
          icon={Info}
          tone="purple"
          active={criticidadFilter === 'BAJO'}
          onClick={() => selectSummaryFilter('BAJO')}
        />
        <EmailStatCard
          label="Informativos"
          value={(emailStats as any)?.byCriticality?.INFORMATIVO ?? 0}
          icon={Info}
          tone="blue"
          active={criticidadFilter === 'INFORMATIVO'}
          onClick={() => selectSummaryFilter('INFORMATIVO')}
        />
      </div>

      {/* ── BARRA DE BÚSQUEDA ── */}
      <div className="flex flex-col sm:flex-row gap-2">
        <SearchBar
          value={search}
          onChange={setSearch}
          onSubmit={(v) => setSearch(v)}
          mode="gmail"
          placeholder="Buscar correos… (from:, subject:, is:unread, has:attachment…)"
        />
        <button
          onClick={() => setShowAdvancedSearch(true)}
          className="inline-flex items-center justify-center w-10 h-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-colors shrink-0"
          title="Búsqueda avanzada"
        >
          <SlidersHorizontal className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          {quickFilters.map(qf => (
            <button
              key={qf.op}
              onClick={() => toggleQuickFilter(qf.op)}
              className={cn(
                'inline-flex items-center gap-1 px-2.5 h-10 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap',
                qf.active
                  ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/30 dark:border-primary-700 dark:text-primary-300'
                  : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700',
              )}
            >
              {qf.label}
              {qf.active && <X className="w-3 h-3" />}
            </button>
          ))}
          <select
            value={limit}
            onChange={(e) => handleLimitChange(Number(e.target.value))}
            className="h-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
            title="Emails por página"
          >
            {limitOptions.map(l => (
              <option key={l} value={l}>{l}/pág</option>
            ))}
          </select>
          <button
            onClick={resetColWidths}
            className="hidden xl:inline-flex items-center gap-1.5 h-10 px-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Restablecer anchos de columna"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── CHIPS DE FILTROS ACTIVOS ── */}
      <FilterChips
        filters={parsedFilters}
        criticidad={criticidadFilter}
        label={labelFilter}
        onRemove={handleRemoveFilter}
        onRemoveCriticidad={() => {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            next.delete('criticidad')
            return next
          })
          setPage(1)
        }}
        onRemoveLabel={() => {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            next.delete('label')
            return next
          })
          setPage(1)
        }}
        onClearAll={handleClearAll}
      />
      </div>{/* cierra header+filtros wrapper */}

      {/* ── LISTA Y VISTA PREVIA ── */}
      <div className={cn(
        selectedEmailId
          ? 'xl:flex xl:gap-0 xl:flex-1 xl:min-h-0'
          : ''
      )}>
      {/* List panel — con sidebar arrastrable */}
      <div
        className={cn('min-w-0', selectedEmailId ? 'xl:shrink-0 xl:w-[var(--list-panel-w)] xl:min-h-0 xl:flex xl:flex-col' : '')}
        style={selectedEmailId ? ({ '--list-panel-w': `${listPanelWidth}px` } as React.CSSProperties) : undefined}
      >
      <div className={cn(
        'bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 min-w-0 overflow-hidden',
        selectedEmailId ? 'xl:flex-1 xl:min-h-0 xl:flex xl:flex-col' : ''
      )}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : !data?.data?.length ? (
          <EmptyResults search={search} onClear={handleClearAll} />
        ) : (
          <>
            {/* Toolbar bulk */}
            {selectedIds.size > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-100 dark:border-blue-800/50">
                <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300">
                  <CheckSquare className="w-4 h-4" />
                  <span className="font-medium">{selectedIds.size} seleccionado{selectedIds.size === 1 ? '' : 's'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <X className="w-3.5 h-3.5" /> Limpiar
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </div>
              </div>
            )}

            {/* Desktop Table with resizable columns — solo cuando NO hay preview abierto */}
            {!selectedEmailId && (
            <div className="hidden xl:block overflow-x-auto">
              <table
                className="text-sm table-fixed"
                style={{
                  width: emailColumns.reduce((sum, c) => sum + (colWidths[c.key] ?? c.defaultWidth), 0),
                  minWidth: '100%',
                }}
              >
                <colgroup>
                  <col style={{ width: colWidths.check }} />
                  <col style={{ width: colWidths.sender }} />
                  <col style={{ width: colWidths.subject }} />
                  <col style={{ width: colWidths.criticidad }} />
                  <col style={{ width: colWidths.date }} />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/50">
                    <th className="relative py-2 px-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      <button onClick={toggleSelectAll} className="p-1" title={selectedIds.size === (data?.data?.length || 0) && (data?.data?.length || 0) > 0 ? 'Deseleccionar página' : 'Seleccionar página'}>
                        {selectedIds.size === (data?.data?.length || 0) && (data?.data?.length || 0) > 0 ? <CheckSquare className="w-4 h-4 text-primary-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                      </button>
                    </th>
                    <th className="relative py-2 px-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      <span>Remitente</span>
                      <div style={{ cursor: 'col-resize' }} className="absolute -right-1 top-0 bottom-0 w-4 flex items-center justify-center z-10"
                      onMouseDown={(e) => onResizeStart(emailColumns[1], e)}>
                      <div className="h-6 w-[3px] rounded-full bg-gray-300 hover:bg-primary-500 transition-colors" />
                    </div>
                    </th>
                    <th className="relative py-2 px-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      <span>Asunto / Vista previa</span>
                      <div style={{ cursor: 'col-resize' }} className="absolute -right-1 top-0 bottom-0 w-4 flex items-center justify-center z-10"
                      onMouseDown={(e) => onResizeStart(emailColumns[2], e)}>
                      <div className="h-6 w-[3px] rounded-full bg-gray-300 hover:bg-primary-500 transition-colors" />
                    </div>
                    </th>
                    <th className="relative py-2 px-2 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      <span>Criticidad</span>
                      <div style={{ cursor: 'col-resize' }} className="absolute -right-1 top-0 bottom-0 w-4 flex items-center justify-center z-10"
                      onMouseDown={(e) => onResizeStart(emailColumns[3], e)}>
                      <div className="h-6 w-[3px] rounded-full bg-gray-300 hover:bg-primary-500 transition-colors" />
                    </div>
                    </th>
                    <th className="relative py-2 px-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      <span>Recibido</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {data.data.map((email: any) => {
                    const isSelected = selectedIds.has(email.id)
                    const fromDisplay = email.fromName || email.from
                    const preview = stripHtml(email.preview || email.body || '').slice(0, 140)
                    const previewHtml = sanitizePreviewHtml(email.htmlBody || email.body || '').slice(0, 500)
                    return (
                      <tr
                        key={email.id}
                        onClick={() => handleOpenEmail(email)}
                        className={cn(
                          'group cursor-pointer transition-colors',
                          'hover:bg-gray-50 dark:hover:bg-gray-800/60',
                          email.isRead ? 'bg-white dark:bg-gray-900' : 'bg-blue-50/30 dark:bg-blue-950/20',
                          isSelected && 'bg-blue-50/70 dark:bg-blue-900/30',
                        )}
                      >
                        {/* Checkbox + star + avatar */}
                        <td className="py-3 px-2 align-middle">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleSelect(email.id) }}
                              className={cn('p-0.5 rounded transition-opacity', isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100')}
                              title={isSelected ? 'Deseleccionar' : 'Seleccionar'}
                            >
                              {isSelected ? <CheckSquare className="w-4 h-4 text-primary-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                            </button>
                            <button onClick={(e) => handleToggleStar(e, email)} className="p-0.5 rounded" title={email.isStarred ? 'Quitar destacado' : 'Destacar'}>
                              <Star className={cn('w-4 h-4 transition-colors', email.isStarred ? 'fill-amber-400 text-amber-400' : 'text-gray-400 opacity-0 group-hover:opacity-100 hover:text-amber-400')} />
                            </button>
                            <div className={cn('w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ring-1 shrink-0', getAvatarColor(fromDisplay), email.isRead ? 'ring-transparent' : 'ring-primary-200')}>
                              {getInitials(fromDisplay)}
                            </div>
                          </div>
                        </td>

                        {/* Remitente */}
                        <td className={cn('py-3 px-2 align-middle min-w-0', !email.isRead && 'font-semibold')}>
                          <p className="truncate text-sm text-gray-900 dark:text-gray-100"><HighlightedText text={fromDisplay} query={search} /></p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">{email.fromName || 'Remitente'}</p>
                        </td>

                        {/* Asunto + Preview */}
                        <td className="py-3 px-2 align-middle min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className={cn('truncate text-sm flex-1', email.isRead ? 'text-gray-700 dark:text-gray-300' : 'font-semibold text-gray-900 dark:text-gray-100')}>
                              <HighlightedText text={email.subject || '(Sin asunto)'} query={search} />
                            </p>
                            {email.hasAttachments && (
                              <Paperclip className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                            )}
                          </div>
                          {previewHtml ? (
                            <div className="email-preview-html mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2 overflow-hidden" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                          ) : preview && (
                            <p className="truncate text-xs text-gray-500 dark:text-gray-400 mt-0.5"><HighlightedText text={preview} query={search} /></p>
                          )}
                        </td>

                        {/* Criticidad y Estado de Gestión */}
                        <td className="py-3 px-2 align-middle text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className={cn('inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-bold truncate max-w-full', criticidadClasses[email.criticidad] || 'bg-slate-100 text-slate-600')}>
                              {formatCriticidad(email.criticidad)}
                            </span>
                            {email.isHandled || email.case?.status?.isFinal ? (
                              <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700 dark:text-emerald-400" title="Gestionado">
                                <CheckCircle2 className="w-2.5 h-2.5" /> Gestionado
                              </span>
                            ) : null}
                          </div>
                        </td>

                        {/* Fecha */}
                        <td className="py-3 px-2 align-middle text-right text-xs text-gray-500 dark:text-gray-400">
                          <span className="tabular-nums">{formatEmailDate(email.date || email.receivedAt)}</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            )}

            {/* Mobile/Tablet List + Desktop cuando preview abierto */}
            {selectedEmailId ? (
              <div className="hidden xl:block xl:flex-1 xl:min-h-0 xl:overflow-y-auto">{renderCompactList()}</div>
            ) : null}
            <div className="xl:hidden">{renderCompactList()}</div>
          </>
        )}
      </div>

      {/* Paginador — fuera del card scrolleable */}
      {totalPages > 1 && (
        <div className={cn(
          'flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0 rounded-b-xl',
        )}>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {((currentPage - 1) * limit) + 1}–{Math.min(currentPage * limit, pagination?.total || 0)} de{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{pagination?.total || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="inline-flex items-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
              title="Anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {getPageNumbers().map((p) => (
              <button
                key={p}
                onClick={() => goToPage(p)}
                className={cn(
                  'inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-md text-sm font-medium',
                  p === currentPage
                    ? 'bg-primary-600 text-white border border-primary-600'
                    : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700',
                )}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
              title="Siguiente"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Divisor arrastrable entre lista y preview */}
      {selectedEmailId && (
        <div
          className="hidden xl:flex items-center justify-center w-5 shrink-0 self-stretch group/split"
          style={{ cursor: 'col-resize' }}
          onMouseDown={handleSplitResizeStart}
          title="Arrastrar para ajustar ancho"
        >
          <div className="w-[3px] h-12 rounded-full bg-gray-300 group-hover/split:bg-primary-500 group-active/split:bg-primary-600 transition-colors" />
        </div>
      )}

      {/* Preview panel */}
      {selectedEmailId && (
        <div className="flex-1 min-w-0 min-h-0">
          <EmailPreviewPanel
            email={selectedEmail}
            isLoading={isSelectedEmailLoading}
            onClose={() => setSelectedEmailId(null)}
            onOpenFull={() => navigate(`/emails/${selectedEmailId}`)}
            updateCriticidad={updateCriticidad}
            toggleHandled={toggleHandled}
            userRole={user?.role}
            successToast={successToast}
            errorToast={errorToast}
          />
        </div>
      )}
      </div>

      <AdvancedSearchModal
        isOpen={showAdvancedSearch}
        onClose={() => setShowAdvancedSearch(false)}
        onSubmit={(query) => {
          setSearch(query)
          setPage(1)
        }}
        initialValue={search}
      />
      </div>{/* cierra flex-1 min-w-0 */}
    </div>
  )
}

function buildSearchFromFilters(f: GmailSearchFilters): string {
  const parts: string[] = []
  if (f.from) parts.push(`from:${f.from}`)
  if (f.to) parts.push(`to:${f.to}`)
  if (f.subject) parts.push(`subject:${f.subject}`)
  if (f.status) parts.push(`status:${f.status}`)
  if (f.label) parts.push(`label:${f.label}`)
  if (f.isUnread) parts.push('is:unread')
  if (f.isRead) parts.push('is:read')
  if (f.isStarred) parts.push('is:starred')
  if (f.hasAttachment) parts.push('has:attachment')
  if (f.hasAlert) parts.push('has:alert')
  if (f.hasCase) parts.push('has:case')
  if (f.afterDate) parts.push(`after:${f.afterDate}`)
  if (f.beforeDate) parts.push(`before:${f.beforeDate}`)
  if (f.body) parts.push(f.body)
  return parts.join(' ')
}

function EmailPreviewPanel({ email, isLoading, onClose, onOpenFull, updateCriticidad, toggleHandled, userRole, successToast, errorToast }: { email: any; isLoading: boolean; onClose: () => void; onOpenFull: () => void; updateCriticidad: any; toggleHandled?: any; userRole?: string; successToast: (msg: string) => void; errorToast: (msg: string) => void }) {
  if (isLoading || !email) {
    return (
      <Card className="min-h-[520px] flex items-center justify-center rounded-xl border-slate-200 shadow-sm">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </Card>
    )
  }

  const isHandled = !!(email.isHandled || (email.case?.status && email.case.status?.isFinal))
  const bodyHtml = email.htmlBody ? sanitizeHtmlBody(email.htmlBody) : ''
  const recipients = Array.isArray(email.to) ? email.to : email.to ? [email.to] : []
  const cc = Array.isArray(email.cc) ? email.cc : email.cc ? [email.cc] : []

  const CRITICITY_ORDER: Record<string, number> = {
    INFORMATIVO: 0, BAJO: 1, MEDIO: 2, ALTO: 3, CRITICO: 4, CRITICO_MAXIMO: 5,
  }
  const currentLevel = CRITICITY_ORDER[email.criticidad || 'INFORMATIVO'] ?? 0
  const caseIsOpen = !!(email.caseId && email.case?.status && !email.case.status?.isFinal)
  const isAdmin = userRole === 'ADMIN'

  const criticityColors: Record<string, { active: string; inactive: string }> = {
    CRITICO_MAXIMO: { active: 'bg-red-600 text-white border-red-600', inactive: 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' },
    CRITICO: { active: 'bg-red-500 text-white border-red-500', inactive: 'bg-red-50 text-red-500 border-red-200 hover:bg-red-100' },
    ALTO: { active: 'bg-orange-500 text-white border-orange-500', inactive: 'bg-orange-50 text-orange-500 border-orange-200 hover:bg-orange-100' },
    MEDIO: { active: 'bg-yellow-500 text-white border-yellow-500', inactive: 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100' },
    BAJO: { active: 'bg-green-500 text-white border-green-500', inactive: 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100' },
    INFORMATIVO: { active: 'bg-blue-500 text-white border-blue-500', inactive: 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100' },
  }
  const criticityLabels: Record<string, string> = {
    CRITICO_MAXIMO: 'Crítico Máx', CRITICO: 'Crítico', ALTO: 'Alto', MEDIO: 'Medio', BAJO: 'Bajo', INFORMATIVO: 'Informativo',
  }

  const handleToggleHandled = () => {
    if (!toggleHandled || toggleHandled.isPending) return
    const nextState = !isHandled
    toggleHandled.mutate(
      { emailId: email.id, isHandled: nextState },
      {
        onSuccess: () => successToast(nextState ? 'Correo marcado como gestionado' : 'Correo marcado como pendiente'),
        onError: (err: any) => errorToast(err?.message || 'Error al actualizar estado de gestión'),
      }
    )
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 min-w-0 h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 bg-white dark:bg-gray-900 shrink-0">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-primary-700 xl:hidden">
          <ArrowLeft className="w-4 h-4" /> Volver a la lista
        </button>
        <span className="hidden xl:block text-xs font-semibold uppercase tracking-wider text-gray-400">Vista previa</span>
        <div className="ml-auto flex items-center gap-2">
          {/* Botón de Estado de Gestión al lado de Ver completo */}
          <button
            onClick={handleToggleHandled}
            disabled={toggleHandled?.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-all shadow-sm',
              isHandled
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-400 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-emerald-950/30'
            )}
            title={isHandled ? 'Correo gestionado (clic para marcar como pendiente)' : 'Marcar este correo como gestionado'}
          >
            {toggleHandled?.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : isHandled ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            ) : (
              <CheckCircle className="w-3.5 h-3.5" />
            )}
            <span>{isHandled ? 'Gestionado' : 'Marcar gestionado'}</span>
          </button>

          <button onClick={onOpenFull} className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-100">
            <ExternalLink className="w-3.5 h-3.5" /> Ver completo
          </button>
          <button onClick={onClose} className="hidden xl:inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:hover:bg-gray-700" title="Cerrar vista previa">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3 shrink-0">
        <button onClick={onOpenFull} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"><Reply className="w-3.5 h-3.5" /> Responder</button>
        <button onClick={onOpenFull} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"><ReplyAll className="w-3.5 h-3.5" /> Responder a todos</button>
        <button onClick={onOpenFull} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"><Forward className="w-3.5 h-3.5" /> Reenviar</button>
        <div className="w-px h-6 bg-gray-200 mx-1 hidden sm:block" />
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden sm:inline">Clasificar</span>
        {(['CRITICO_MAXIMO', 'CRITICO', 'ALTO', 'MEDIO', 'BAJO', 'INFORMATIVO'] as const).map((level) => {
          const isActive = email.criticidad === level
          const levelOrder = CRITICITY_ORDER[level] ?? 0
          const isDegradation = levelOrder < currentLevel
          const isBlocked = caseIsOpen && isDegradation && !isAdmin
          return (
            <button
              key={level}
              onClick={() => {
                if (isActive || isBlocked || updateCriticidad.isPending) return
                updateCriticidad.mutate(
                  { emailId: email.id, criticidad: level },
                  {
                    onSuccess: () => successToast(`Clasificado como ${criticityLabels[level]}`),
                    onError: (err: any) => errorToast(err?.message || 'Error al clasificar'),
                  }
                )
              }}
              disabled={updateCriticidad.isPending || isBlocked}
              className={cn(
                'px-2 py-1 text-[10px] font-semibold rounded-md border transition-all',
                isActive ? criticityColors[level].active : criticityColors[level].inactive,
                (updateCriticidad.isPending || isBlocked) && 'opacity-40 cursor-not-allowed',
                isBlocked && 'line-through',
              )}
              title={isBlocked ? 'Degradación bloqueada por caso abierto' : criticityLabels[level]}
            >
              {criticityLabels[level]}
            </button>
          )
        })}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-5 space-y-4">
          <div className="border-b border-gray-100 pb-4">
            <h2 className="text-lg font-semibold leading-snug text-gray-900 dark:text-gray-100">{email.subject || '(Sin asunto)'}</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', email.isProcessed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700')}>
                {email.isProcessed ? 'Procesado' : 'Sin procesar'}
              </span>
              <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold', isHandled ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' : 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-200 dark:border-amber-800')}>
                {isHandled ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Clock className="w-3 h-3 text-amber-600" />}
                {isHandled ? (email.case?.status?.isFinal ? 'Gestionado (Caso cerrado)' : (email.handledBy ? `Gestionado` : 'Gestionado')) : 'Pendiente de gestión'}
              </span>
              {email.criticidad && <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', criticidadClasses[email.criticidad] || 'bg-slate-100 text-slate-600')}>{formatCriticidad(email.criticidad)}</span>}
            </div>
          </div>

          <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-start gap-2"><User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" /><span className="w-12 shrink-0 font-semibold text-gray-500">De:</span><span className="break-all font-medium text-gray-900 dark:text-gray-100">{email.from}</span></div>
            <div className="flex items-start gap-2"><Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" /><span className="w-12 shrink-0 font-semibold text-gray-500">Para:</span><span className="break-all text-primary-700">{recipients.join(', ') || '—'}</span></div>
            {cc.length > 0 && <div className="flex items-start gap-2"><Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" /><span className="w-12 shrink-0 font-semibold text-gray-500">CC:</span><span className="break-all">{cc.join(', ')}</span></div>}
            <div className="flex items-start gap-2"><Calendar className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" /><span className="w-12 shrink-0 font-semibold text-gray-500">Fecha:</span><span>{email.date ? new Date(email.date).toLocaleString('es-CO') : '—'}</span></div>
          </div>

          <div className="border-t border-gray-200 pt-5">
            {bodyHtml ? (
              <div className="prose prose-sm max-w-none break-words text-gray-800 dark:text-gray-200 email-body-content" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
            ) : (
              <div className="whitespace-pre-wrap break-words text-sm leading-7 text-gray-800 dark:text-gray-200">{email.body || 'Sin contenido'}</div>
            )}
          </div>

          {email.attachments?.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <p className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Archivos adjuntos ({email.attachments.length})</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {email.attachments.map((attachment: any) => (
                  <a key={attachment.id || attachment.filename} href={attachment.url} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700 hover:border-primary-300 hover:bg-primary-50">
                    <Paperclip className="h-4 w-4 shrink-0 text-primary-600" />
                    <span className="truncate">{attachment.filename || 'Adjunto'}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function EmailStatCard({
  label,
  value,
  icon: Icon,
  tone,
  active = false,
  onClick,
}: {
  label: string
  value: number
  icon: any
  tone: 'blue' | 'slate' | 'red' | 'amber' | 'green' | 'purple'
  active?: boolean
  onClick: () => void
}) {
  return (
    <IndicatorCard
      label={label}
      value={value}
      icon={Icon}
      tone={tone}
      active={active}
      onClick={onClick}
    />
  )
}

function EmptyResults({ search, onClear }: { search: string; onClear: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Archive className="w-12 h-12 text-gray-300 dark:text-gray-700 mb-3" aria-hidden="true" />
      <p className="text-base font-medium text-gray-900 dark:text-gray-100">
        No hay emails que coincidan
      </p>
      {search && (
        <>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            Ningún resultado para: <code className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">{search}</code>
          </p>
          <button
            onClick={onClear}
            className="mt-3 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            Limpiar búsqueda
          </button>
        </>
      )}
    </div>
  )
}
